import { useEffect, useState } from "react";
import {
  addNewBanner,
  fetchBannerById,
  updatedBanner,
  saveBannerBgImage,
  fetchEntities,
} from "../../services/axios/apiManager";
import { useNavigate, useParams } from "react-router-dom";
import { handleErrors } from "../../services/dataManager";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import DashboardLayout from "../DashboardLayout";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SwitchButtonMedium } from "../../Components/switchButton";
import { baseURL } from "../../services/axios/eps";

const AddEditBanner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [banner, setBanner] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [moduleData, setModuleData] = useState([]);
  const [imageChanged, setImageChanged] = useState(false);

  const getData = async () => {
    const res = await fetchBannerById(id);
    if (res?.data?.success && res?.data?.data) {
      setBanner(res.data.data);
      setImageURL(null);
      setImageChanged(false);
    } else {
      handleErrors(res, navigate, "Home Banner: ");
    }
    setLoading(false);
  };

  const handleSwitchBtn = () => {
    if (formik.values.button_enabled === 1) {
      formik.setFieldValue("button_enabled", 0);
    } else {
      formik.setFieldValue("button_enabled", 1);
    }
  };

  const validateFile = (file, type) => {
    const maxSize = type === "video" ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for video, 10MB for image
    const allowedImageTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/jpg",
      "image/avif",
    ];
    const allowedVideoTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/quicktime",
    ];

    if (file.size > maxSize) {
      toast.error(
        `File size exceeds the limit (${type === "video" ? "50MB" : "10MB"}).`,
      );
      return false;
    }

    if (type === "video") {
      if (!allowedVideoTypes.includes(file.type)) {
        toast.error(
          "Invalid video format. Please upload MP4, WebM, OGG, or MOV.",
        );
        return false;
      }
    } else {
      if (!allowedImageTypes.includes(file.type)) {
        toast.error(
          "Invalid image format. Please upload PNG, WebP, or JPEG/JPG.",
        );
        return false;
      }
    }

    return true;
  };

  const makeImageURL = (file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImageURL(previewUrl);
      setImageChanged(true);
    }
  };

  const getModuleData = async (module) => {
    if (module === "other") return;
    const res = await fetchEntities(module);
    if (res?.data?.success && res.data?.data) {
      setModuleData(res.data.data);
    } else {
      handleErrors(res, navigate, "Add/Edit Banners: ");
    }
  };
  const formik = useFormik({
    initialValues: {
      banner_type: banner?.banner_type || "image",
      heading: banner?.heading || "",
      sub_heading: banner?.sub_heading || "",
      details: banner?.details || "",
      bg_image: banner?.bg_image || "",
      button_enabled: banner?.button_enabled || 0,
      button_text: banner?.button_text || "",
      button_url: banner?.button_url || "",
      status: banner?.status || 1,
      module_type: banner.module_type || "other",
      module_id: banner.module_id || "",
      bread_crumbs: "",
      content: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      heading: Yup.string().required("Heading is required"),
      sub_heading: Yup.string().required("Sub heading is required"),
      details: Yup.string().required("Details are required"),
      bg_image: Yup.mixed().required("Background image or video is required"),
      button_text: Yup.string()
        .nullable()
        .when("button_enabled", {
          is: (val) => val === 1 || val === true,
          then: () =>
            Yup.string().required("Button text is required").nullable(),
          otherwise: () => Yup.string().nullable(),
        }),
      button_url: Yup.string()
        .nullable()
        .when("button_enabled", {
          is: (val) => val === 1 || val === true,
          then: () =>
            Yup.string().required("Button url is required").nullable(),
          otherwise: () => Yup.string().nullable(),
        }),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        let res;
        const hasNewFile = values.bg_image instanceof File;

        const postData = {
          ...values,
          bg_image: hasNewFile ? banner.bg_image || "" : values.bg_image,
        };
        console.log("Post Data :", postData);

        if (id) {
          res = await updatedBanner({ id, ...postData });
          if (res?.data?.success) {
            if (hasNewFile) {
              const formData = new FormData();
              formData.append("image", values.bg_image);
              const uploadRes = await saveBannerBgImage(id, formData);
              if (!uploadRes?.data?.success) {
                toast.error(
                  uploadRes?.data?.message || "Failed to upload file",
                );
                setSubmitting(false);
                return;
              }
            }
            toast.success("Banner updated successfully");
            navigate("/home-banner");
          } else {
            toast.error(res?.data?.message || "Failed to update banner");
          }
        } else {
          res = await addNewBanner(postData);
          if (res?.data?.success && res.data.data?.id) {
            const newId = res.data.data.id;
            if (hasNewFile) {
              const formData = new FormData();
              formData.append("image", values.bg_image);
              const uploadRes = await saveBannerBgImage(newId, formData);
              if (!uploadRes?.data?.success) {
                toast.error(
                  uploadRes?.data?.message || "Failed to upload file",
                );
                setSubmitting(false);
                return;
              }
            }
            toast.success("Banner created successfully");
            navigate("/home-banner");
          } else {
            toast.error(res?.data?.message || "Failed to create banner");
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while saving");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getData();
    } else {
      setLoading(false);
    }
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="row">
          <div className="col-12 mb-3">
            <div className="page-title-box d-sm-flex">
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a>Management</a>
                  </li>
                  <li
                    className="breadcrumb-item active"
                    style={{ color: "#8b8b8cbf" }}
                  >
                    {`${id ? "Edit" : "Add"} Home Banners`}
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="align-items-center mt-5 p-4 rounded w-100 bg-dark">
          <Form onSubmit={formik.handleSubmit}>
            <Row className="mb-4">
              <Col xs={12} md={12}>
                <Form.Label>Banner Type</Form.Label>
                <Form.Select
                  data-testid="banner-type"
                  className="bg-dark text-light border-light input-placeholder-light"
                  style={{ paddingTop: "9px", paddingBottom: "9px" }}
                  name="banner_type"
                  value={formik.values.banner_type}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue("bg_image", "");
                    setImageURL(null);
                    setImageChanged(false);
                  }}
                  isInvalid={
                    formik.touched.banner_type && !!formik.errors.banner_type
                  }
                >
                  <option value="image">Image</option>
                  <option value="video" disabled={true}>
                    Video
                  </option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.banner_type}
                </Form.Control.Feedback>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col xs={12} md={12}>
                <Form.Label>Heading</Form.Label>
                <Form.Control
                  type="text"
                  data-testid="heading"
                  className="bg-dark text-light border-light input-placeholder-light py-4"
                  placeholder="Enter Heading"
                  {...formik.getFieldProps("heading")}
                  isInvalid={formik.touched.heading && !!formik.errors.heading}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.heading}
                </Form.Control.Feedback>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col xs={12} md={12}>
                <Form.Label>Sub-Heading</Form.Label>
                <Form.Control
                  type="text"
                  data-testid="sub-heading"
                  className="bg-dark text-light border-light input-placeholder-light py-4"
                  placeholder="Enter Sub-Heading"
                  {...formik.getFieldProps("sub_heading")}
                  isInvalid={
                    formik.touched.sub_heading && !!formik.errors.sub_heading
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.sub_heading}
                </Form.Control.Feedback>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col className="sm-12 md-12">
                <Form.Label>Details*</Form.Label>
                <Form.Control
                  as={"textarea"}
                  rows={4}
                  placeholder="Enter Details"
                  name="details"
                  data-testid="details"
                  className="bg-dark text-light border-light py-2 input-placeholder-light"
                  {...formik.getFieldProps("details")}
                  isInvalid={formik.touched.details && !!formik.errors.details}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.details}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <div className="container-fluid py-2 px-0 mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="mb-0 mx-2 text-light font-semibold">
                  Show Button
                </Form.Label>
                <SwitchButtonMedium
                  isOn={formik.values.button_enabled === 1}
                  color={"#00af43ff"}
                  onClick={() => {
                    handleSwitchBtn();
                  }}
                />
                <span
                  className={`mx-3 text-sm font-medium ${formik.values.button_enabled === 1 ? "text-success" : "text-muted"}`}
                  style={{ fontSize: "0.9rem" }}
                >
                  {formik.values.button_enabled === 1
                    ? "Yes (Display a button)"
                    : "No Button"}
                </span>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateRows:
                  formik.values.button_enabled === 1 ? "1fr" : "0fr",
                transition:
                  "grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  minHeight: "0px",
                  transition:
                    "opacity 0.3s ease-in-out, padding 0.35s ease-in-out",
                  opacity: formik.values.button_enabled === 1 ? 1 : 0,
                  paddingBottom:
                    formik.values.button_enabled === 1 ? "1.5rem" : "0px",
                }}
              >
                <Row className="mb-4">
                  <Col xs={12} md={6}>
                    <Form.Label>Button Text</Form.Label>
                    <Form.Control
                      type="text"
                      data-testid="button-text"
                      className="bg-dark text-light border-light input-placeholder-light py-4"
                      placeholder="Enter Button text"
                      {...formik.getFieldProps("button_text")}
                      isInvalid={
                        formik.touched.button_text &&
                        !!formik.errors.button_text
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.button_text}
                    </Form.Control.Feedback>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Label>Module Type</Form.Label>
                    <Form.Select
                      data-testid="module-type"
                      className="bg-dark text-light border-light input-placeholder-light"
                      style={{ paddingTop: "9px", paddingBottom: "9px" }}
                      name="module_type"
                      value={formik.values.module_type}
                      onChange={async (e) => {
                        formik.setFieldValue("module_id", "");
                        formik.setFieldValue("button_url", "");
                        formik.setFieldValue("module_type", e.target.value);
                        await getModuleData(e.target.value);
                      }}
                      isInvalid={
                        formik.touched.module_type &&
                        !!formik.errors.module_type
                      }
                    >
                      <option value="other">Other / Custom URL</option>
                      <option value="games">Games</option>
                      <option value="custom">Custom Pages</option>
                      <option value="charitable_organization">
                        Charitable Organizations
                      </option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.module_type}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                {(formik.values.module_type === "games" ||
                  formik.values.module_type === "charitable_organization") && (
                  <Row className="mb-4">
                    <Col xs={12} md={12}>
                      <Form.Label>{formik.values.module_type === "games" ?"Select Game":"Select Charitable Organization"}</Form.Label>
                      <Form.Select
                        data-testid="module-id"
                        className="bg-dark text-light border-light input-placeholder-light"
                        style={{ paddingTop: "9px", paddingBottom: "9px" }}
                        name="module_id"
                        value={formik.values.module_id}
                        onChange={(e) => {
                          formik.setFieldValue("module_id", e.target.value);
                          formik.setFieldValue(
                            "button_url",
                            `${formik.values.module_type}/${e.target.value}`,
                          );
                        }}
                        isInvalid={
                          formik.touched.module_id && !!formik.errors.module_id
                        }
                      >
                        <option value="">Select an option</option>
                        {moduleData?.map((mod) => (
                          <option key={mod.id} value={mod?.slug || mod.id}>
                            {mod?.name || mod?.title || mod?.heading}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.module_id}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                )}
                <Row className="mb-4">
                  <Col xs={12} md={12}>
                    <Form.Label>Button URL</Form.Label>
                    <Form.Control
                      type="text"
                      data-testid="button-url"
                      className="bg-dark text-light border-light input-placeholder-light py-4"
                      placeholder="Enter Button URL"
                      disabled={
                        formik.values.module_type === "games" ||
                        formik.values.module_type === "charitable_organization"
                      }
                      {...formik.getFieldProps("button_url")}
                      isInvalid={
                        formik.touched.button_url && !!formik.errors.button_url
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.button_url}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </div>
            </div>

            <Row className="mb-4">
              <Col xs={12} md={12}>
                <Form.Label>
                  {formik.values.banner_type === "video" ? "Video*" : "Image*"}
                </Form.Label>
                <Form.Control
                  type="file"
                  data-testid="bg-image"
                  accept={
                    formik.values.banner_type === "video"
                      ? "video/mp4,video/webm,video/ogg,video/quicktime"
                      : "image/png,image/jpeg,image/webp,image/jpg"
                  }
                  className="bg-dark text-light border-light"
                  onChange={(event) => {
                    if (event?.currentTarget?.files?.length) {
                      const file = event.currentTarget.files[0];
                      if (!validateFile(file, formik.values.banner_type)) {
                        event.currentTarget.value = "";
                        return;
                      }
                      formik.setFieldValue("bg_image", file);
                      makeImageURL(file);
                    } else {
                      formik.setFieldValue("bg_image", banner?.bg_image || "");
                      setImageURL(null);
                      setImageChanged(false);
                    }
                  }}
                  isInvalid={
                    formik.touched.bg_image && !!formik.errors.bg_image
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.bg_image}
                </Form.Control.Feedback>
                <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                  {formik.values.banner_type === "video"
                    ? "Maximum file size: 50MB. Formats: MP4, WebM, OGG, MOV."
                    : "Maximum file size: 10MB. Formats: PNG, WebP, JPEG, JPG."}
                </span>

                {(imageURL || formik.values.bg_image) && (
                  <div className="mt-3">
                    <Form.Label>Preview</Form.Label>
                    <div style={{ display: "block" }}>
                      {formik.values.banner_type === "video" ? (
                        <video
                          src={
                            imageURL
                              ? imageURL
                              : formik.values.bg_image
                                ? `${baseURL}/uploads/file/${formik.values.bg_image}`
                                : null
                          }
                          controls
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "5px",
                          }}
                        />
                      ) : (
                        <img
                          crossOrigin="anonymous"
                          src={
                            imageURL
                              ? imageURL
                              : formik.values.bg_image
                                ? `${baseURL}/uploads/file/${formik.values.bg_image}`
                                : null
                          }
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "5px",
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate("/home-banner")}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={submitting}>
                {submitting
                  ? "Saving..."
                  : id
                    ? "Save Changes"
                    : "Create Section"}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </DashboardLayout>
  );
};
export default AddEditBanner;
